import * as React from 'react';
import { Link, useHistory } from 'react-router-dom';

import { Field, FieldProps, FormikProps } from 'formik';
import Select from 'antd/lib/select';
import Checkbox from 'antd/lib/checkbox';
import Tooltip from 'antd/lib/tooltip';

import Autocomplete from '@common/react/components/Forms/Autocomplete/Autocomplete';
import { ZipCode } from '@common/typescript/objects/ZipCode';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { FormikPhoneControl } from '@common/react/components/Forms/FormikPhoneControl/FormikPhoneControl';
import { State } from '@common/react/components/Forms/State/State';
import { parseQuery } from '@common/typescript/utils/url';

import { DatePickerReact } from '@common/react/components/UI/DatePickerReact/DatePickerReact';

import { GenderNames } from '@app/objects/Gender';
import { RegistrationUser, User } from '@app/objects/User';
import { request } from '@app/components/Api';
import { Location } from '@app/objects/Location';

type FormValues = RegistrationUser | User;

interface ComponentProps {
	formikBag: FormikProps<FormValues>;
}

const SelectOption = Select.Option;

const PASSWORD_REQUIREMENTS_MESSAGE = 'Password should be at least 8 characters long';

const DemographicInfoRegistration: React.FC<ComponentProps> = ({ formikBag }) => {
	const [isInvite, setIsInvite] = React.useState(false);
	const history = useHistory();

	React.useEffect(() => {
		const query = parseQuery(history.location.search);
		if (query.userId && query.token) {
			getUserByInviteToken(+query.userId, query.token);
		}
	}, []);

	const getUserByInviteToken = (userId: number, token: string) => {
		request<User>('getUserByInviteToken', { userId, token })
			.then((res) => {
				setIsInvite(true);
				formikBag.setValues({
					...formikBag.values,
					id: res.id,
					email: res.email,
					firstName: res.firstName,
					lastName: res.lastName,
					birthDate: res.birthDate,
					phoneNumber: res.phoneNumber,
					patientLocations: res.patientLocations,
					insurances: res.insurances,
					drivingLicenses: res.drivingLicenses,
				});
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const NameRender = ({ form, field }: FieldProps) =>
		<input
			className="form-control"
			type="text"
			id={field.name}
			maxLength={50}
			value={field.value}
			onChange={(value) => form.setFieldValue(field.name, value.currentTarget.value.replace(/[^a-z]/gi, ''))}
		/>;

	const renderPatientInfo = (formikBag: FormikProps<FormValues>) => {
		return <>
			<div className="row">
				<FormikField
					fieldName="firstName"
					title="First Name*"
					containerClassName="form-group col-sm-3"
					render={NameRender}
				/>
				<FormikField
					fieldName="lastName"
					title="Last Name*"
					containerClassName="form-group col-sm-3"
					render={NameRender}
				/>
				<FormikField
					fieldName="gender"
					title="Gender*"
					containerClassName="form-group col-sm-3"
					render={({ field, form }: FieldProps<FormValues>) =>
						<Select
							id={field.name}
							value={field.value?.toString()}
							onChange={(value) => form.setFieldValue(field.name, +value)}
						>
							{Object.keys(GenderNames).map((key) =>
								<SelectOption value={key} key={key}>{GenderNames[key]}</SelectOption>)}
						</Select>
					}
				/>
				<FormikField
					fieldName="birthDate"
					title="Birth Date*"
					containerClassName="form-group col-sm-3"
					render={({ field, form }) => (
						<DatePickerReact
							value={field.value}
							onChange={(date) => form.setFieldValue(field.name, date)}
							datePickerProps={{
								showMonthDropdown: true,
								showYearDropdown: true,
								dropdownMode: 'select',
							}}
							utc
						/>
					)}
				/>
			</div>
			<div className="form-grey-block clearfix">
				<div className="row">
					<div className="col-sm-12">
						<FormikField
							fieldName="zip"
							title="Zip*"
							containerClassName="form-group col-sm-2"
							render={(fieldProps) => (
								<Autocomplete<ZipCode>
									type="zipCodeListRemote"
									renderTitle="zip"
									loadOnFocus
									onSelect={(value, option) => {
										formikBag.setValues({
											...formikBag.values,
											zip: option.props.item.zip,
											city: option.props.item.city,
											region: option.props.item.stateName,
										});
									}}
									onChange={(value = '') => formikBag.setFieldValue(fieldProps.field.name, value)}
									value={fieldProps.field.value}
								/>
							)}
						/>
						<FormikField
							fieldName="city"
							title="City*"
							containerClassName="form-group col-sm-2"
						/>
						<FormikField
							fieldName="region"
							title="State*"
							containerClassName="form-group col-sm-3"
							render={({ field, form }) => (
								<State onChange={(state) => form.setFieldValue(field.name, state)} value={field.value} />
							)}
						/>
						<FormikField
							fieldName="address"
							title="Address"
							containerClassName="form-group col-sm-5"
						/>
					</div>
				</div>
			</div>
			<div className="row">
				<FormikField
					fieldName="email"
					title="Email*"
					containerClassName="form-group col-sm-3"
					inputProps={{ disabled: isInvite }}
				/>
				<FormikField
					containerClassName="form-group col-sm-3"
					fieldName="phoneNumber"
					title="Cell Phone*"
					render={(fieldProps) => <FormikPhoneControl fieldProps={fieldProps} placeholder="+1 (___) ___-____" />}
				/>
				<FormikField
					fieldName="patientLocations"
					title="Clinic"
					containerClassName="form-group col-sm-6"
					render={({ field, form }: FieldProps<FormValues>) => (
						<Autocomplete<Location>
							type="locationList"
							renderTitle={(item: Location) => `${item?.company ? `${item.company.name} - ` : ''}${item.nameEn}`}
							onSelect={(value, option) => {
								formikBag.setFieldValue(field.name, [{
									locationId: +value,
									locationName: `${option.props.item?.company
										? `${option.props.item.company.name} - `
										: ''}${option.props.item.nameEn}`,
								}]);
							}}
							onChange={(value = '') => {
								if (value === '') {
									formikBag.setFieldValue(field.name, []);
								}
							}}
							antdProps={{
								dropdownClassName: 'long-dropdown',
							}}
							value={formikBag.values.patientLocations[0] ? formikBag.values.patientLocations[0].locationName : ''}
							loadOnFocus
							disabled={isInvite}
						/>)
					}
				/>
			</div>
			<div className="row">
				<FormikField
					fieldName="password"
					title="Password*"
					inputProps={{ type: 'password' }}
					render={({ form, field }: FieldProps, inputProps?: React.HTMLProps<HTMLInputElement>) => (
						<Tooltip placement="topLeft" trigger="focus" title={PASSWORD_REQUIREMENTS_MESSAGE}>
							<input className="form-control" type="text" id={field.name} {...field} {...inputProps} />
						</Tooltip>
					)}
				/>
				<FormikField fieldName="repeatPassword" title="Repeat Password*" inputProps={{ type: 'password' }} />
			</div>
		</>;
	};

	const renderEmergencyContact = () => {
		return <>
			<div className="row">
				<FormikField
					fieldName="emergencyContactName"
					title="Name"
					containerClassName="form-group col-sm-4"
					render={NameRender}
				/>
				<FormikField
					title="Phone"
					containerClassName="form-group col-sm-4"
					fieldName="emergencyContactPhone"
					render={(fieldProps) => <FormikPhoneControl fieldProps={fieldProps} placeholder="+1 (___) ___-____" />}
				/>
				<FormikField
					fieldName="emergencyContactRelation"
					title="Relation"
					containerClassName="form-group col-sm-4"
					render={NameRender}
				/>
			</div>
		</>;
	};

	return (
		<>
			<div className="form-box">
				<div className="register-headline text-center form-box__headline">
					<h1>REGISTRATION</h1>
				</div>
				<div className="form-box__body">
					{renderPatientInfo(formikBag)}
				</div>
			</div>
			<div className="form-box">
				<div className="form-box__headline">Emergency Contact</div>
				<div className="form-box__body">
					{renderEmergencyContact()}
				</div>
			</div>
			<div className="form-box">
				<div className="form-box__body">
					<div className="row">
						<div className="form-group col-sm-12">
							<label>
								Do we have your permission to contact you for appointment reminders|promotions and specials via:
							</label>
							<div>
								<Field name="allowSms" render={({ field }) => <Checkbox {...field} checked={field.value}> Text </Checkbox>} />
								<Field name="allowEmail" render={({ field }) => <Checkbox {...field} checked={field.value}> Email </Checkbox>} />
								<Field name="allowPush" render={({ field }) => <Checkbox {...field} checked={field.value}> Push </Checkbox>} />
								<Field name="allowCalls" render={({ field }) => <Checkbox {...field} checked={field.value}> Calls </Checkbox>} />
							</div>
						</div>
					</div>
					<div className="row">
						<div className="form-group col-sm-12">
							By clicking “Create Account” I agree to the&nbsp;
							<a className="privacy-policy-text" href="/privacy-policy" target="_blank">Privacy Policy</a>
							<div className="link pull-right">
								<Link to="/login">Already have an account?</Link>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default DemographicInfoRegistration;
